import type { FC } from 'react';
import { memo, useCallback, useMemo, useRef } from 'react';
import CartItemOptions from '../CartItemOptions';
import QuantityBox from './QuantityBox';
import CartItemButtons from './CartItemButtons';
import DisplayComment from './Comment/DisplayComment';
import Price from './Price';
import type { OrderLineDto } from '../../../../../../generated-api';
import DisplayGiftCardPhoneNumber from './GiftCardPhoneNumber/DisplayGiftCardPhoneNumber';
import classNames from 'clsx';
import { useActiveSearchableSections } from '../../../../../context/MenuSectionsStore';
import { useLocation } from '../../../../../context/LocationContext';
import { convertGramsToItemUnit, getUnitLabel } from '../../../../../utils/barcodeParser';

interface Props {
  orderLine: OrderLineDto;
  itemIndex: number;
  setActiveItem: (index?: number) => void;
  isActive: boolean;
  giftCardPhoneNumber?: string;
  className?: string;
}

const CartItem: FC<Props> = ({ orderLine, itemIndex, setActiveItem, isActive, giftCardPhoneNumber, className }) => {
  const ref = useRef<HTMLLIElement>(null);
  const { location } = useLocation();
  const menuSections = useActiveSearchableSections(location.zoneId);

  const handlePressItem = useCallback(() => {
    setActiveItem(itemIndex);
  }, [setActiveItem, itemIndex]);

  const liClassName = useMemo(
    () =>
      classNames('py-2', className, {
        'bg-purple-lightest': isActive,
      }),
    [className, isActive]
  );

  // Find menu item to get priceType for weight display
  const menuItem = useMemo(() => {
    if (!orderLine.unitQuantity || !orderLine.openPrice) {
      return null;
    }
    return menuSections
      .flatMap((section) => section.items)
      .find((item) => item.variantLinkId === orderLine.itemVariantId);
  }, [menuSections, orderLine.itemVariantId, orderLine.unitQuantity, orderLine.openPrice]);

  const unitLabel = menuItem?.priceType ? getUnitLabel(menuItem.priceType) : null;
  const hasWeight = orderLine.unitQuantity != null && orderLine.unitQuantity > 0 && unitLabel;

  // Convert unitQuantity from grams back to product's unit for display
  const displayQuantity =
    hasWeight && menuItem?.priceType && orderLine.unitQuantity
      ? convertGramsToItemUnit(orderLine.unitQuantity, menuItem.priceType)
      : null;

  const hasOptions = orderLine.options.length > 0;
  const hasGiftCardPhoneNumber = orderLine.giftCardId && giftCardPhoneNumber;
  const hasComment = orderLine.comment && orderLine.comment.length > 0;

  return (
    <li
      ref={ref}
      className={liClassName}
      role="button"
      tabIndex={0}
      aria-label="Change quantity"
      onClick={handlePressItem}
    >
      <div className="flex items-center gap-2" data-cy="cart-line">
        <QuantityBox isActive={isActive} orderLine={orderLine} />
        <div className="max-w-xs">
          <div className="text-bold text-small truncate-2" data-cy="cart-line-name">
            {orderLine.name}
          </div>
          {hasWeight && displayQuantity !== null && (
            <div className="text-xs text-grey-dark">
              {displayQuantity} {unitLabel}
            </div>
          )}
          {hasOptions && <CartItemOptions options={orderLine.options} />}
          {hasGiftCardPhoneNumber && <DisplayGiftCardPhoneNumber phone={giftCardPhoneNumber} />}
          {hasComment && <DisplayComment comment={orderLine.comment ?? ''} />}
        </div>
        <Price orderLine={orderLine} />
      </div>
      {isActive && <CartItemButtons orderLine={orderLine} />}
    </li>
  );
};

export default memo(CartItem);
